import React, { useEffect, useState } from 'react';
import { Trans, useI18nContext } from '@ecg-marktplaats/js-react-i18n';
import { BasicModal, Block, Card, Icon, Link, Text } from '@hz-design-system/web-ui';
import classNames from 'classnames';

import EmailInfo from './components/EmailInfo';
import LocationInfo from './components/LocationInfo';
import OpeningHours from './components/OpeningHours';
import OpeningHoursTodayFormatted from './components/OpeningHoursTodayFormatted';
import ProfileImage from './components/ProfileImage';
import ProfileStaticMap from './components/ProfileStaticMap';
import SalesRepresentativesBlock from './components/SalesRepresentativesBlock';
import SellerCompanyLegalInfo from './components/SellerCompanyLegalInfo';
import WebsiteUrl from './components/WebsiteUrl';

import {
  SellerGeolocation,
  SellerProfile,
  SellerProfileCompanyRegistrationData,
} from '../../../../../types/SellerProfile';
import redirectToSellerWebsiteUrl from '../../utils/redirectToSellerWebsiteUrl';
import { today } from '../../utils/weekDays';
import getIsMobile from '../../utils/isMobile';
import { TOAST_AUTOCLOSE_TIMER } from '../../constants/constants';

import Classes from './ProfileSidebar.scss';

function isHoursSet<T extends Record<any, unknown>>(openingHours: T | undefined) {
  return openingHours && Object.keys(openingHours).some((key) => openingHours[key]);
}

type ProfileSideBarProps = {
  businessHours?: Record<
    any,
    {
      openFrom: string;
      openTill: string;
      closed: boolean;
    }
  >;
  outsideBusinessHourAvailable: SellerProfile['outsideBusinessHourAvailable'];
  callAvailabilityHours?: Record<
    any,
    {
      openFrom: string;
      openTill: string;
      closed: boolean;
    }
  >;
  companyRegistrationData: SellerProfileCompanyRegistrationData;
  contactInformation: {
    city: string;
    postCode: string;
    street: string;
    phoneNumber: string;
    email?: string;
  };
  dsaFlags: {
    publicInfoProvided: boolean;
    approved: boolean;
  };
  hasGeolocation: boolean;
  salesRepresentatives: any[];
  sellerGeolocation: SellerGeolocation;
  sellerId: number;
  sellerTermsAndConditions?: string;
  sellerTitle: string;
  logoImage: string;
  websiteUrl: string;
};
const ProfileSidebar = (props: ProfileSideBarProps) => {
  const {
    dsaFlags,
    sellerId,
    sellerTitle,
    logoImage,
    contactInformation: { email, city, postCode, street, phoneNumber } = {},
    businessHours,
    outsideBusinessHourAvailable,
    callAvailabilityHours,
    hasGeolocation,
    sellerGeolocation,
    websiteUrl,
    salesRepresentatives = [],
    companyRegistrationData,
  } = props;

  const i18n = useI18nContext();

  const [isBusinessHoursPopupOpen, setBusinessHoursOpen] = useState(false);
  const [isCallAvailabilityHoursPopupOpen, setCallAvailabilityHoursOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [copyMessageVisible, setCopyMessageVisible] = useState(false);

  useEffect(() => {
    setIsMobile(getIsMobile());
  }, []);

  const handleCopyPhoneNumber = () => {
    if (phoneNumber) {
      navigator.clipboard.writeText(phoneNumber);
      setCopyMessageVisible(true);
      setTimeout(() => {
        setCopyMessageVisible(false);
      }, TOAST_AUTOCLOSE_TIMER);
    }
  };

  const businessHoursToday = businessHours && businessHours[today];
  const callAvailabilityHoursToday = callAvailabilityHours && callAvailabilityHours[today];

  return (
    <>
      {businessHours && isBusinessHoursPopupOpen && (
        <BasicModal
          title={i18n.t('sellerProfile.businessHours.label')}
          isBottomSheet
          onClose={() => setBusinessHoursOpen(false)}
        >
          <Block className={Classes.content}>
            <OpeningHours openingHours={businessHours} />
          </Block>
        </BasicModal>
      )}
      {callAvailabilityHours && isCallAvailabilityHoursPopupOpen && (
        <BasicModal
          title={i18n.t('sellerProfile.callAvailabilityHours.label')}
          isBottomSheet
          onClose={() => setCallAvailabilityHoursOpen(false)}
        >
          <Block className={Classes.content}>
            <OpeningHours openingHours={callAvailabilityHours} />
          </Block>
        </BasicModal>
      )}
      {Boolean(logoImage || websiteUrl || phoneNumber || email || isHoursSet(callAvailabilityHours)) && (
        <Card kind="rounded" className={Classes.wrap}>
          <div className="u-textStyleBodyRegularStrong u-paddingXAxisS u-paddingTopS">
            {i18n.t('sellerProfile.contactInformation')}
          </div>
          <Block className={Classes.cardSection}>
            {logoImage && <ProfileImage logoImage={logoImage} sellerTitle={sellerTitle} />}
            <ul>
              {websiteUrl && <WebsiteUrl url={redirectToSellerWebsiteUrl(sellerId)} />}
              {Boolean(phoneNumber || isHoursSet(callAvailabilityHours)) && (
                <>
                  <li className={`${Classes.infoWrap} ${Classes.infoWrapWithMultiline}`}>
                    <Icon name="call" />
                    <div className={Classes.infoMultiline}>
                      {Boolean(phoneNumber) && (
                        <span className={Classes.phoneNumberContainer}>
                          {isMobile ? (
                            <Link href={`tel:${phoneNumber}`}>{phoneNumber}</Link>
                          ) : (
                            <button onClick={() => handleCopyPhoneNumber()} className={Classes.phoneNumber}>
                              {phoneNumber}
                            </button>
                          )}
                          {copyMessageVisible && (
                            <span className={Classes.copyMessage}>
                              {i18n.t('sellerProfile.phoneNumber.copySuccessMessage')}
                            </span>
                          )}
                        </span>
                      )}
                      {isHoursSet(callAvailabilityHours) && (
                        <>
                          {callAvailabilityHoursToday && (
                            <OpeningHoursTodayFormatted
                              businessHoursToday={callAvailabilityHoursToday}
                              labelKey={'sellerProfile.callAvailabilityHours.todayFromTill'}
                            />
                          )}
                          <Link onClick={() => setCallAvailabilityHoursOpen(true)}>
                            {i18n.t('sellerProfile.seeCallAvailabilityHours.label')}
                          </Link>
                        </>
                      )}
                    </div>
                  </li>
                </>
              )}
              {dsaFlags.approved && email && <EmailInfo email={email} />}
            </ul>
          </Block>
          <SalesRepresentativesBlock salesRepresentatives={salesRepresentatives} />
        </Card>
      )}
      {(Boolean(postCode && street && city) || hasGeolocation || isHoursSet(businessHours)) && (
        <Card kind="rounded" className={Classes.wrap}>
          <div className={Classes.card}>
            {postCode && street && city && (
              <div className={Classes['cardSection-address']}>
                <LocationInfo sellerAddress={{ postCode, street, city }} />
              </div>
            )}
            {isHoursSet(businessHours) && (
              <div className={Classes.cardSection}>
                <div className={Classes.openingHoursWrap}>
                  {businessHoursToday && (
                    <div className={Classes.openingHours}>
                      <Icon name="clock" className={Classes['openingHours-icon']} />
                      <div className={Classes.openingHoursText}>
                        <OpeningHoursTodayFormatted
                          businessHoursToday={businessHoursToday}
                          labelKey={'sellerProfile.businessHours.todayFromTill'}
                        />
                        <div className={Classes.linkWrapper}>
                          <Link onClick={() => setBusinessHoursOpen(true)}>
                            {i18n.t('sellerProfile.seeBusinessHours.label')}
                          </Link>
                        </div>
                        {outsideBusinessHourAvailable && (
                          <Trans
                            i18nKey="sellerProfile.outsideBusinessHourAvailable"
                            tagName="div"
                            className={`u-colorTextSecondary ${Classes['sellerCompanyLegalInfo-itemLabel']}`}
                          />
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            {hasGeolocation && (
              <ProfileStaticMap latitude={sellerGeolocation.latitude} longitude={sellerGeolocation.longitude} />
            )}
          </div>
        </Card>
      )}
      {dsaFlags.approved && (
        <Card kind="rounded" className={Classes.wrap}>
          <div className={Classes.card}>
            <div className={classNames(Classes.cardSection, Classes.alignCenter)}>
              <Icon className={Classes.dsaApprovedIcon} name="checkmark-circled" />
              <Text.Regular>{i18n.t('sellerProfile.dsa.approved')}</Text.Regular>
            </div>
          </div>
        </Card>
      )}
      {companyRegistrationData && <SellerCompanyLegalInfo sellerCompanyLegalInfo={companyRegistrationData} />}
    </>
  );
};

export default ProfileSidebar;
